import {BrowserModule} from '@angular/platform-browser';
import {APP_INITIALIZER, NgModule, CUSTOM_ELEMENTS_SCHEMA, ApplicationConfig, Injector} from '@angular/core';
import { firstValueFrom } from 'rxjs';
import { filter, take, timeout } from 'rxjs/operators';
import {AppRoutingModule} from './app-routing.module';
import {AppComponent} from './app.component';
import {AddlinkModule} from '@ems/addlink';
import {EmtrModule} from '@ems/emtr';
import {EmsShellModule} from '@ui_module/ems-shell-module/ems-shell.module';
import { HTTP_INTERCEPTORS, provideHttpClient, withInterceptorsFromDi } from '@angular/common/http';
import {FormsModule, ReactiveFormsModule} from '@angular/forms';
import {EmsComponent} from './ems/ems.component';
import {MaintenanceComponent} from '@shell_components/core/maintenance/maintenance.component';
import {HttpConfigInterceptor} from '@core_services/interceptor/httpConfig.interceptor';
import {SharedModule} from '@ems/shared';
import {BrowserAnimationsModule, provideAnimations} from '@angular/platform-browser/animations';
import {commonConstants} from '@shell_components/constants/commonConstants';
import { entitlementConst } from '@shell_components/constants/entitlementConstants';
import {AppInitService} from '@shell_components/app-init.service';
import {DynamicEnvironment} from '@environment/dynamic-environment';
import { providePrimeNG } from 'primeng/config';
import Aura from '@primeng/themes/aura';
import { definePreset } from '@primeng/themes';
import { UploadModule } from '@schwab/safeui';
import { FEATURE_TOGGLE_CONFIGURATION_PROPERTIES, FeatureToggleClientService, SchwabFeatureToggleAngularModule } from '@schwab/schwab-feature-toggle-angular';
import { featureToggleConfigurationPropertiesFactory } from './feature-toggle/feature-toggle.config';

/** Time (ms) to wait for the feature-toggle client before failing safe. */
const FEATURE_TOGGLE_READY_TIMEOUT_MS = 3000;

export function init_app(appLoadService: AppInitService, injector: Injector) {
  // 1. Load runtime config first; it provides the LaunchDarkly client-side ID
  //    used by SchwabFeatureToggleAngularModule when the feature-toggle client
  //    is first injected (below, after config is available).
  // 2. Then wait for the feature-toggle client to become ready so route guards
  //    (`evaluateFlag`) and the maintenance watcher (`monitorFlag`) read the
  //    real flag value at startup instead of the default. Reading it before the
  //    client connects returned the default and caused a redirect flip-flop.
  // The whole step is fail-safe: any error (client construction, readiness) or
  // the timeout simply lets startup proceed with flags at their defaults
  // (maintenance off). The feature-toggle client must never block or crash
  // application startup.
  return () => appLoadService.init().then(async () => {
    try {
      const featureToggleClient = injector.get(FeatureToggleClientService);
      if (featureToggleClient.isReady()) {
        return;
      }
      await firstValueFrom(
        featureToggleClient.monitorReadiness().pipe(
          filter(ready => ready === true),
          take(1),
          timeout(FEATURE_TOGGLE_READY_TIMEOUT_MS)
        )
      );
    } catch {
      // Feature-toggle unavailable or slow: proceed with default flag values.
    }
  });
}

const MyPreset = definePreset(Aura, {
    semantic: {
        primary: {
            50: '{sky.50}',
            100: '{sky.100}',
            200: '{sky.200}',
            300: '{sky.300}',
            400: '{sky.400}',
            500: '{sky.500}',
            600: '{sky.600}',
            700: '{sky.700}',
            800: '{sky.800}',
            900: '{sky.900}',
            950: '{sky.950}'
        }
        // Removed colorScheme - let CSS custom properties handle all colors
    }
});
@NgModule({ declarations: [
        AppComponent,
        EmsComponent,
        MaintenanceComponent
    ],
    exports: [],
    schemas: [
        CUSTOM_ELEMENTS_SCHEMA
    ],
    bootstrap: [AppComponent], imports: [BrowserModule,
        FormsModule,
        ReactiveFormsModule,
        BrowserAnimationsModule,
        AppRoutingModule,
        AddlinkModule,
        EmtrModule,
        EmsShellModule,
        UploadModule,
        SchwabFeatureToggleAngularModule.forRoot(),
        SharedModule.forRoot(commonConstants, entitlementConst)], providers: [
        { provide: HTTP_INTERCEPTORS, useClass: HttpConfigInterceptor, multi: true },
        {
            provide: APP_INITIALIZER,
            useFactory: init_app,
            deps: [AppInitService, Injector],
            multi: true
        },
        {
            provide: 'env', useValue: DynamicEnvironment
        },
        {
            provide: FEATURE_TOGGLE_CONFIGURATION_PROPERTIES,
            useFactory: featureToggleConfigurationPropertiesFactory
        },
        provideHttpClient(withInterceptorsFromDi()),
        provideAnimations(),
        providePrimeNG({
            theme: {
                preset: MyPreset,
                options: {
                    darkModeSelector: false, // Disable PrimeNG's dark mode - use our custom CSS system
                },
            },
        })
    ] })
export class AppModule {
}
